import { Vue, Component } from 'vue-property-decorator';
import createAuth0Client, {
  Auth0Client,
  RedirectLoginOptions,
  GetIdTokenClaimsOptions,
  GetTokenSilentlyOptions,
  LogoutOptions,
  Auth0ClientOptions
} from '@auth0/auth0-spa-js';
import type { GenericError } from '@auth0/auth0-spa-js/dist/typings/errors';
import { User } from './types/user';
import axios from 'axios';
import { Auth0Options } from '@/auth/types/auth0Options';
import { RedirectCallback } from '@/auth/types/auth0PluginOptions';
import webpubsub from '@/api/webpubsub';
import { trackingMod } from '@/store/modules/tracking';
import { routeMod } from '@/store/modules/route';
import { TrackableAssetStateChangedMessage } from '@/api/types';
import { authMod } from '@/store/modules/auth';
import { getParsedJwt } from '@/common/utils/jwt';
import { IVueAuth, VueAuthErrorCode } from '@/auth/index';

@Component({})
export class VueAuth extends Vue implements IVueAuth {
  loading = true;
  isAuthenticated? = false;
  user?: User;
  auth0Client?: Auth0Client;
  error?: Error;
  errorCode: VueAuthErrorCode | null = null;

  async getUser() {
    return new User(await this.auth0Client?.getUser());
  }

  /** Authenticates the user using the redirect method */
  loginWithRedirect(o: RedirectLoginOptions) {
    return this.auth0Client?.loginWithRedirect(o);
  }

  /** Returns all the claims present in the ID token */
  getIdTokenClaims(o: GetIdTokenClaimsOptions) {
    return this.auth0Client?.getIdTokenClaims(o);
  }

  /** Returns the access token. If the token is invalid or missing, a new one is retrieved */
  getTokenSilently(o: GetTokenSilentlyOptions) {
    return this.auth0Client?.getTokenSilently(o);
  }

  /** Logs the user out and removes their session on the authorization server */
  logout(o: LogoutOptions) {
    return this.auth0Client?.logout(o);
  }

  public async setUserPermission(): Promise<void> {
    const accessToken = await this.$auth.getTokenSilently({});
    const decodedToken = getParsedJwt<any>(accessToken!);
    axios.defaults.headers.common['Authorization'] = 'Bearer ' + accessToken;

    await authMod.setPermissions(decodedToken.permissions);
    await authMod.setAuthAccessToken(accessToken!);
  }

  public async getUserObjectId(): Promise<string | undefined> {
    return this.user!.objectId;
  }

  /** Use this lifecycle method to instantiate the SDK client */
  async init(onRedirectCallback: RedirectCallback, redirectUri: string, auth0Options: Auth0Options) {
    const options: Auth0ClientOptions = {
      domain: auth0Options.domain,
      client_id: auth0Options.clientId,
      audience: auth0Options.audience,
      redirect_uri: redirectUri
    };

    if (auth0Options.organizationId) {
      options.organization = auth0Options.organizationId;
    }

    // Create a new instance of the SDK client using members of the given options object
    this.auth0Client = await createAuth0Client(options);

    try {
      // If the user is returning to the app after authentication..

      if (
        window.location.search.includes('error=') ||
        (window.location.search.includes('code=') && window.location.search.includes('state='))
      ) {
        // handle the redirect and retrieve tokens
        const { appState } = (await this.auth0Client?.handleRedirectCallback()) ?? {
          appState: undefined
        };

        // Notify subscribers that the redirect callback has happened, passing the appState
        // (useful for retrieving any pre-authentication state)
        onRedirectCallback(appState);
      }
    } catch (e: unknown) {
      if (e instanceof Error) {
        if ('error' in e) {
          const auth0Error = e as GenericError;
          if (
            auth0Error.error === 'access_denied' &&
            auth0Error.error_description.includes('is not part of the org_')
          ) {
            this.errorCode = 'invalid_organisation';
          } else if (auth0Error.error === 'invalid_state') {
            this.errorCode = 'invalid_state';
          } else {
            this.errorCode = 'generic_error';
          }
          this.error = e;
        } else {
          this.errorCode = 'generic_error';
          this.error = e;
        }
      } else if (typeof e === 'string') {
        this.errorCode = 'generic_error';
        this.error = new Error(e);
      } else {
        this.errorCode = 'generic_error';
        this.error = new Error('Something went wrong');
      }
    } finally {
      // Initialize our internal authentication state when the page is reloaded
      this.isAuthenticated = await this.auth0Client?.isAuthenticated();
      this.user = await this.getUser();
      this.loading = false;

      const response = await webpubsub.GetClientAccessUri();

      const ws = new WebSocket(response);

      ws.onmessage = function (message) {
        const data: TrackableAssetStateChangedMessage = JSON.parse(message.data);

        trackingMod.setTrackableAssetState(data);
        routeMod.updateRouteTrackableAssetStates(data);
      };

      ws.onerror = function (data) {
        console.log(data);
      };
    }
  }
}
